<template>
	<validation-provider
		ref="provider"
		v-slot="slotProps"
		:rules="fieldRules"
	>
		<a-form-item
			v-if="loaded"
			:label="labelItem"
			has-feedback
			class="mr-0 ant-form-item-gestios"
			:class="{
				'ant-form-item-required': attrs.required,
				'ant-form-item-clean mb-0': clean,
				'mb-3': !clean}"
			:validate-status="errors ? $utils.resolveState(slotProps) : ''"
			:help="!clean ? slotProps.errors[0] : ''"
		>
			<!-- TEXT -->
			<a-input
				v-if="attrs.type === 'text'"
				:value="value"
				:disabled="disabled"
				:placeholder="attrs.placeholder"
				class="w-100"
				:max-length="attrs.maxlength"
				:size="size"
				@change="textChange"
			/>

			<a-textarea
				v-if="attrs.type === 'textarea'"
				:value="value"
				:disabled="disabled"
				:placeholder="attrs.placeholder"
				class="w-100"
				:max-length="attrs.maxlength"
				:rows="rows"
				:size="size"
				@change="textChange"
			/>

			<!-- INTEGER -->
			<a-input-number
				v-else-if="attrs.type === 'integer'"
				:value="value"
				:disabled="disabled"
				:placeholder="attrs.placeholder"
				class="w-100"
				:min="attrs.min || undefined"
				:max="attrs.max || undefined"
				:step="attrs.step"
				:formatter="numberFormatter"
				:parser="numberParser"
				:size="size"
				@change="numberChange"
			/>

			<!-- DOUBLE -->
			<a-input-number
				v-else-if="attrs.type === 'double'"
				:value="value"
				:disabled="disabled"
				:placeholder="attrs.placeholder"
				class="w-100"
				:min="attrs.min || undefined"
				:max="attrs.max || undefined"
				:step="attrs.step"
				decimal-separator=","
				:formatter="numberFormatter"
				:parser="numberParser"
				:precision="attrs.decimals"
				:size="size"
				@change="numberChange"
			/>

			<!-- SELECT -->
			<a-select
				v-else-if="attrs.type === 'select'"
				ref="select"
				:disabled="disabled"
				:value="value"
				:allow-clear="true"
				:placeholder="attrs.placeholder"
				class="w-100"
				:size="size"
				@change="selectChange"
			>
				<a-select-option
					v-for="(d, i) in selectOptions"
					:key="i"
					:value="d.key"
				>
					{{ d.label }}
				</a-select-option>
			</a-select>

			<!-- RELATION -->
			<a-select
				v-else-if="attrs.type === 'relation' && strict"
				ref="relation"
				:disabled="disabled"
				label-in-value
				:value="relationModelValue"
				:allow-clear="!required"
				:placeholder="attrs.placeholder"
				class="w-100"
				:filter-option="false"
				:not-found-content="dataLoading ? undefined : null"
				show-search
				:mode="attrs.multiple ? 'multiple' : 'default'"
				:size="size"
				@search="relationFetch($event, true)"
				@focus="relationFetch($event, true)"
				@change="relationChange($event, 'input')"
			>
				<a-spin
					v-if="dataLoading"
					slot="notFoundContent"
					size="small"
				/>
				<a-select-option
					v-for="d in dataSource"
					:key="d.value.toString()"
				>
					{{ d.text }}
				</a-select-option>
			</a-select>
			<a-auto-complete
				v-else-if="attrs.type === 'relation' && !strict"
				:value="value"
				:placeholder="attrs.placeholder"
				class="w-100"
				:size="size"
				@search="relationFetch($event, false)"
				@change="relationSimpleChange($event, 'input')"
				@select="relationSimpleChange($event, 'select')"
			>
				<template slot="dataSource">
					<a-select-option
						v-for="(item, index) in dataSource"
						:key="index"
						:value="item.value"
					>
						{{ item.text }}
					</a-select-option>
				</template>
			</a-auto-complete>

			<!-- USER -->
			<a-select
				v-else-if="attrs.type === 'user'"
				ref="user"
				:disabled="disabled"
				label-in-value
				:value="userValue"
				:allow-clear="!required"
				:placeholder="attrs.placeholder"
				class="w-100"
				:filter-option="false"
				:not-found-content="dataLoading ? undefined : null"
				show-search
				option-label-prop="label"
				:size="size"
				@search="userFetch"
				@focus="userFetch"
				@change="userChange"
			>
				<a-spin
					v-if="dataLoading"
					slot="notFoundContent"
					size="small"
				/>
				<a-select-option
					v-for="d in data"
					:key="d.value.toString()"
					:label="d.text"
				>
					<a-avatar
						:src="$gestios.avatars(d.data.ID)"
						shape="square"
						class="mr-2"
						:size="25"
					/>
					{{ d.text }}
				</a-select-option>
			</a-select>

			<a-date-picker
				v-else-if="attrs.type === 'date'"
				:value="dateValue"
				:disabled="disabled"
				:placeholder="attrs.placeholder"
				class="w-100"
				:format="attrs.dateFormat"
				:size="size"
				@change="dateChange"
			/>
			<a-time-picker
				v-else-if="attrs.type === 'time'"
				:value="timeValue"
				:disabled="disabled"
				:placeholder="attrs.placeholder"
				class="w-100"
				:format="attrs.timeFormat"
				:size="size"
				@change="dateChange"
			/>

			<a-checkbox
				v-else-if="attrs.type === 'boolean' && booleanType === 'checkbox'"
				:checked="booleanValue"
				:size="size"
				@change="booleanChange"
			>
				{{ attrs.placeholder }}
			</a-checkbox>
		</a-form-item>
	</validation-provider>
</template>

<script>
import _ from 'lodash';

import attrs from './_attrs';
import parser from './_parser';
import rules from './_rules';
import synced from './_sync';

import relationModule from './types/_relation';
import userModule from './types/_user';
import selectModule from './types/_select';
import dateModule from './types/_date';
import numberModule from './types/_number';
import textModule from './types/_text';
import booleanModule from './types/_boolean';

export default {
	name: 'GField',
	mixins: [
		attrs, parser, rules, synced, relationModule,
		userModule, selectModule, dateModule, numberModule,
		textModule, booleanModule
	],
	props: {
		// //////////////////
		// VUEX DATA
		// //////////////////
		app: {
			type: String,
			default: null
		},
		item: {
			type: Number,
			default: 0
		},
		name: {
			type: String,
			required: true
		},
		size: {
			type: String,
			default: 'default'
		},
		// //////////////////
		// VALUES
		// //////////////////
		value: {
			required: false,
			default: null
		},
		defaultValue: {
			default: null,
			required: false
		},
		// //////////////////
		// TEXT DISPLAY
		// //////////////////
		title: {
			type: String,
			default: ''
		},
		placeholder: {
			type: String,
			default: null
		},
		// //////////////////
		// RULES, TYPE & CONDITIONS
		// //////////////////
		type: {
			type: String,
			validator: (val) => ['text', 'integer', 'double', 'textarea', 'select', 'relation', 'date', 'datetime', 'time', 'user', 'boolean'].includes(val),
			default: null
		},
		required: {
			type: Boolean,
			default: null
		},
		disabled: {
			type: Boolean,
			default: false
		},
		rules: {
			type: Array,
			default: null
		},
		// //////////////////
		// STYLE
		// //////////////////
		clean: {
			type: Boolean,
			default: false
		},
		errors: {
			type: Boolean,
			default: true
		}
	},
	data() {
		return {
			loaded: false,
			data: [],
			valid: null
		};
	},
	computed: {
		labelItem() {
			if (this.type === 'boolean') return ' ';
			return this.title;
		}
	},
	watch: {
		value(newval, oldval) {
			if (JSON.stringify(newval) !== JSON.stringify(oldval)) {
				const parsedValue = this.parseValue(newval);
				this.$refs.provider.syncValue(parsedValue);
				this.$emit('input', parsedValue);
			}
		},
		item(val) {
			this.$refs.provider.applyResult({
				errors: [],
				valid: true,
				failedRules: {}
			});

			if (val === null || val === 0) {
				this.$emit('input', this.parseDefaultValue());
			} else {
				this.init();
			}
		}
	},
	created() {
		this.init();
		this.$store.subscribe((mutation) => {
			if (mutation.type === 'OS_WIPE_FORM' && mutation.payload === this.form) {
				this.$emit('input', this.parseDefaultValue());
			}
		});
	},
	beforeDestroy() {
		this.loaded = false;
		// this.$emit('input', this.parseDefaultValue());
	},
	mounted() {
		this.loaded = true;
	},
	methods: {
		init() {
			let value;

			if (this.item > 0) {
				let item = null;

				if (this.app) item = this.fetchVault();

				if (item !== null && (item[this.name] || item[this.name] === 0)) {
					value = this.parseValue(item[this.name]);
				} else if (this.value || this.value === 0) {
					value = this.parseValue(this.value);
				} else {
					value = this.parseDefaultValue();
				}
			} else if (this.value !== undefined && this.value !== null) {
				value = this.parseValue(this.value);
			} else {
				value = this.parseDefaultValue();
			}

			this.$emit('input', value);
		},
		fetchVault() {
			const item = this.$gestios.$cache.items(this.app).get(this.item);
			if (item) return _.cloneDeep(item);
			return null;
		}
	}
};
</script>

<style lang="scss" scoped>
.ant-form-item-clean {
	&::before {
		display: none;
	}
}
</style>
